/* eslint-disable fp/no-mutating-methods */

import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { groupBy, uniq } from 'ramda'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  Class,
  Course,
  Section,
  Series,
  LinkedServiceSeries,
  HeroSlide,
} from 'src/v3/core/types'
import { materializeCourseProgress } from 'src/v3/self-paced/utils/course-progress'
import {
  isActionableProgress,
  sortByUrgency,
} from 'src/v3/self-paced/utils/progress-display'
import { CourseProgressSummary, CourseProgressSummaryWire } from './types'

export let fetchSections = createAsyncThunk(
  'selfPaced/fetchSectionsStatus',
  () => axios.get<Section[]>('/api/v2/self-paced/sections').then(pluckData),
)

export let fetchClasses = createAsyncThunk('selfPaced/fetchClassesStatus', () =>
  axios.get<Class[]>('/api/v2/self-paced/classes').then(pluckData),
)

export let fetchCourses = createAsyncThunk('selfPaced/fetchCoursesStatus', () =>
  axios.get<Course[]>('/api/v2/self-paced/courses').then(pluckData),
)

export let fetchAssignments = createAsyncThunk(
  'selfPaced/fetchAssignments',
  () => axios.get<string[]>('/api/v2/self-paced/assignments').then(pluckData),
)

export let fetchIncompleteAssignmentCourses = createAsyncThunk(
  'selfPaced/fetchIncompleteAssignmentCourses',
  () =>
    axios
      .get<string[]>('/api/self-paced/assignment-courses/incomplete')
      .then(pluckData),
)

export let fetchCompletedClasses = createAsyncThunk(
  'selfPaced/fetchCompletedClasses',
  () =>
    axios.get<string[]>('/api/v2/self-paced/classes/completed').then(pluckData),
)

export let fetchProfileImage = createAsyncThunk(
  'selfPaced/fetchProfileImage',
  () =>
    axios.get<string>('/api/v2/self-paced/user/profile-image').then(pluckData),
)

export let fetchSeries = createAsyncThunk('selfPaced/fetchSeries', () =>
  axios.get<Series[]>('/api/v2/self-paced/series').then(pluckData),
)

export let fetchHeroSlides = createAsyncThunk('selfPaced/fetchHeroSlides', () =>
  axios.get<HeroSlide[]>('/api/self-paced/hero-slides').then(pluckData),
)

export let fetchLinkedServiceSeries = createAsyncThunk(
  'selfPaced/fetchLinkedServiceSeries',
  () =>
    axios
      .get<LinkedServiceSeries[]>('/api/v3/self-paced/linked-service-series')
      .then(pluckData),
)

export let fetchCourseProgressSummaries = createAsyncThunk(
  'selfPaced/fetchCourseProgressSummaries',
  () =>
    axios
      .get<CourseProgressSummaryWire[]>('/api/v2/self-paced/progress')
      .then(pluckData)
      .then((wire) => wire.map(materializeCourseProgress)),
)

let groupBySection = groupBy((c: Course) => c.sectionID)

type CreateState = {
  sectionsStatus: Status
  sections: Section[]
  classesStatus: Status
  classes: Class[]
  coursesStatus: Status
  courses: Course[]
  completedClassesStatus: Status
  completedClasses: string[]
  assignmentsStatus: Status
  assignments: string[]
  incompleteAssignmentCoursesStatus: Status
  incompleteAssignmentCourses: string[]
  profileImageStatus: Status
  profileImage: string
  seriesStatus: Status
  series: Series[]
  linkedServiceSeriesStatus: Status
  linkedServiceSeries: LinkedServiceSeries[]
  heroSlidesStatus: Status
  heroSlides: HeroSlide[]
  courseProgressStatus: Status
  courseProgress: CourseProgressSummary[]
  // Set once on the first fulfilled fetch and never reset — consumers gate
  // their skeleton on this rather than on courseProgressStatus, since this
  // endpoint is refetched on every landing in self-paced and a refetch's
  // 'pending' status must not reflash a skeleton over stale-but-good data.
  courseProgressHasLoadedOnce: boolean
}

export let initialState: CreateState = {
  sectionsStatus: 'uninitialized',
  sections: [],
  classesStatus: 'uninitialized',
  classes: [],
  coursesStatus: 'uninitialized',
  courses: [],
  completedClassesStatus: 'uninitialized',
  completedClasses: [],
  assignmentsStatus: 'uninitialized',
  assignments: [],
  incompleteAssignmentCoursesStatus: 'uninitialized',
  incompleteAssignmentCourses: [],
  profileImageStatus: 'uninitialized',
  profileImage: '',
  seriesStatus: 'uninitialized',
  series: [],
  linkedServiceSeriesStatus: 'uninitialized',
  linkedServiceSeries: [],
  heroSlidesStatus: 'uninitialized',
  heroSlides: [],
  courseProgressStatus: 'uninitialized',
  courseProgress: [],
  courseProgressHasLoadedOnce: false,
}

export let selfPacedSlice = createSlice({
  name: 'selfPaced',
  initialState,
  reducers: {
    sectionsChanged: (state, action: PayloadAction<Section[]>) => {
      state.sections = action.payload
    },
    coursesChanged: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload
    },
    classesChanged: (state, action: PayloadAction<Class[]>) => {
      state.classes = action.payload
    },
    completedClassesChanged: (state, action: PayloadAction<string[]>) => {
      state.completedClasses = action.payload
    },
    assignmentsChanged: (state, action: PayloadAction<string[]>) => {
      state.assignments = action.payload
    },
    profileImageChanged: (state, action: PayloadAction<string>) => {
      state.profileImage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSections.pending, (state) => {
        state.sectionsStatus = 'pending'
      })
      .addCase(fetchSections.rejected, (state) => {
        state.sectionsStatus = 'rejected'
      })
      .addCase(fetchSections.fulfilled, (state, action) => {
        state.sectionsStatus = 'fulfilled'
        state.sections = action.payload
      })
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchClasses.pending, (state) => {
        state.classesStatus = 'pending'
      })
      .addCase(fetchClasses.rejected, (state) => {
        state.classesStatus = 'rejected'
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.classesStatus = 'fulfilled'
        state.classes = action.payload
      })
      .addCase(fetchCompletedClasses.pending, (state) => {
        state.completedClassesStatus = 'pending'
      })
      .addCase(fetchCompletedClasses.rejected, (state) => {
        state.completedClassesStatus = 'rejected'
      })
      .addCase(fetchCompletedClasses.fulfilled, (state, action) => {
        state.completedClassesStatus = 'fulfilled'
        state.completedClasses = action.payload
      })
      .addCase(fetchAssignments.pending, (state) => {
        state.assignmentsStatus = 'pending'
      })
      .addCase(fetchAssignments.rejected, (state) => {
        state.assignmentsStatus = 'rejected'
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignmentsStatus = 'fulfilled'
        state.assignments = action.payload
      })
      .addCase(fetchIncompleteAssignmentCourses.pending, (state) => {
        state.incompleteAssignmentCoursesStatus = 'pending'
      })
      .addCase(fetchIncompleteAssignmentCourses.rejected, (state) => {
        state.incompleteAssignmentCoursesStatus = 'rejected'
      })
      .addCase(fetchIncompleteAssignmentCourses.fulfilled, (state, action) => {
        state.incompleteAssignmentCoursesStatus = 'fulfilled'
        state.incompleteAssignmentCourses = action.payload
      })
      .addCase(fetchProfileImage.pending, (state) => {
        state.profileImageStatus = 'pending'
      })
      .addCase(fetchProfileImage.rejected, (state) => {
        state.profileImageStatus = 'rejected'
      })
      .addCase(fetchProfileImage.fulfilled, (state, action) => {
        state.profileImageStatus = 'fulfilled'
        state.profileImage = action.payload
      })
      .addCase(fetchSeries.pending, (state) => {
        state.seriesStatus = 'pending'
      })
      .addCase(fetchSeries.rejected, (state) => {
        state.seriesStatus = 'rejected'
      })
      .addCase(fetchSeries.fulfilled, (state, action) => {
        state.seriesStatus = 'fulfilled'
        state.series = action.payload
      })
      .addCase(fetchHeroSlides.pending, (state) => {
        state.heroSlidesStatus = 'pending'
      })
      .addCase(fetchHeroSlides.rejected, (state) => {
        state.heroSlidesStatus = 'rejected'
      })
      .addCase(fetchHeroSlides.fulfilled, (state, action) => {
        state.heroSlidesStatus = 'fulfilled'
        state.heroSlides = action.payload
      })
      .addCase(fetchLinkedServiceSeries.pending, (state) => {
        state.linkedServiceSeriesStatus = 'pending'
      })
      .addCase(fetchLinkedServiceSeries.rejected, (state) => {
        state.linkedServiceSeriesStatus = 'rejected'
      })
      .addCase(fetchLinkedServiceSeries.fulfilled, (state, action) => {
        state.linkedServiceSeriesStatus = 'fulfilled'
        state.linkedServiceSeries = action.payload
      })
      .addCase(fetchCourseProgressSummaries.pending, (state) => {
        state.courseProgressStatus = 'pending'
      })
      .addCase(fetchCourseProgressSummaries.rejected, (state) => {
        state.courseProgressStatus = 'rejected'
      })
      .addCase(fetchCourseProgressSummaries.fulfilled, (state, action) => {
        state.courseProgressStatus = 'fulfilled'
        state.courseProgress = action.payload
        state.courseProgressHasLoadedOnce = true
      })
  },
})

export let {
  sectionsChanged,
  classesChanged,
  coursesChanged,
  completedClassesChanged,
  assignmentsChanged,
  profileImageChanged,
} = selfPacedSlice.actions

export let selectSections = (state: AppRootState) => state.selfPaced.sections
export let selectSectionsStatus = (state: AppRootState) =>
  state.selfPaced.sectionsStatus
export let selectSectionById = (id: string) => (state: AppRootState) =>
  state.selfPaced.sections.find((s) => s.id === id)
export let selectSectionsByLinkedService =
  (id: string) => (state: AppRootState) => [
    ...state.selfPaced.sections.filter((s) => s.linkedServiceID == null),
    ...state.selfPaced.sections.filter((s) => s.linkedServiceID === id),
  ]

export let selectSectionsSorted = (state: AppRootState) => {
  let sections = (selectSections(state) ?? []).map((section) => {
    let index = 9999
    if (section.name === 'Airway/Respiratory/Ventilation') {
      index = 0
    } else if (section.name === 'Cardiovascular') {
      index = 1
    } else if (section.name === 'Trauma') {
      index = 2
    } else if (section.name === 'Medical') {
      index = 3
    } else if (section.name === 'Pediatrics') {
      index = 4
    } else if (section.name === 'Operations') {
      index = 5
    } else if (section.name === 'Critical Care') {
      index = 6
    }
    return { ...section, index }
  })

  return sections.sort((a, b) => a.index - b.index)
}

export let selectManySectionsById = (ids: string[]) => (state: AppRootState) =>
  selectSectionsSorted(state).filter((s) => ids.includes(s.id))

export let selectCourses = (state: AppRootState) => state.selfPaced.courses
export let selectCoursesStatus = (state: AppRootState) =>
  state.selfPaced.coursesStatus
export let selectCourseById = (id: string) => (state: AppRootState) =>
  state.selfPaced.courses.find((c) => c.id === id)
export let selectCoursesBySection = (id: string) => (state: AppRootState) =>
  state.selfPaced.courses
    .filter((c) => c.sectionID === id)
    .filter((c) => c.published === 'published')
export let selectCoursesByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.selfPaced.courses.filter((c) => c.linkedServiceID === id)
export let selectCoursesBySectionAndLinkedService =
  (sectionID: string, linkedServiceID: string) => (state: AppRootState) =>
    state.selfPaced.courses.filter(
      (c) => c.sectionID === sectionID && c.linkedServiceID === linkedServiceID,
    )
export let selectManyCoursesById = (ids: string[]) => (state: AppRootState) =>
  state.selfPaced.courses.filter((c) => ids.includes(c.id))
export let selectManyCoursesByIdGroupedBySection =
  (ids: string[]) => (state: AppRootState) =>
    groupBySection(selectManyCoursesById(ids)(state))

// Derived from the progress rollup rather than its own fetch — `state ===
// 'completed'` there is the exact same backend derivation the old
// /courses/completed endpoint used (latest CourseStreamEvent), so this is a
// like-for-like replacement, not an approximation. Order-independent of
// where selectCourseProgress is defined below: this function body only reads
// it when actually called, well after module evaluation finishes.
export let selectCompletedCourses = (state: AppRootState) =>
  state.selfPaced.courseProgress
    .filter((p) => p.state === 'completed')
    .map((p) => p.courseID)

export let selectCoursesGroupedBySection = (state: AppRootState) => {
  let completedCourses = selectCompletedCourses(state)

  return groupBySection(
    state.selfPaced.courses
      .filter((c) => c.published === 'published')
      .sort((a: Course, b: Course) => {
        if (
          completedCourses.some((cc) => cc === a.id) &&
          !completedCourses.some((cc) => cc === b.id)
        )
          return 1
        if (
          !completedCourses.some((cc) => cc === a.id) &&
          completedCourses.some((cc) => cc === b.id)
        )
          return -1
        return 0
      }),
  )
}

export let selectClasses = (state: AppRootState) => state.selfPaced.classes
export let selectClassById = (id: string) => (state: AppRootState) =>
  state.selfPaced.classes.find((c) => c.id === id)
export let selectManyClassesById = (ids: string[]) => (state: AppRootState) =>
  state.selfPaced.classes.filter((c) => ids.includes(c.id))
export let selectClassesStatus = (state: AppRootState) =>
  state.selfPaced.classesStatus
export let selectClassesByCourseID = (id: string) => (state: AppRootState) =>
  state.selfPaced.classes.filter((c) => c.courseID === id)
export let selectClassesGroupedByCourseID = (state: AppRootState) =>
  groupBy((c: Class) => c.courseID)(state.selfPaced.classes)

export let selectCompletedClasses = (state: AppRootState) =>
  state.selfPaced.completedClasses
export let selectCompletedClassesStatus = (state: AppRootState) =>
  state.selfPaced.completedClassesStatus

export let selectAssignments = (state: AppRootState) =>
  state.selfPaced.assignments
export let selectAssignmentsStatus = (state: AppRootState) =>
  state.selfPaced.assignmentsStatus

export let selectIncompleteAssignmentCourses = (state: AppRootState) =>
  state.selfPaced.incompleteAssignmentCourses
export let selectIncompleteAssignmentCoursesStatus = (state: AppRootState) =>
  state.selfPaced.incompleteAssignmentCoursesStatus

export let selectProfileImage = (state: AppRootState) =>
  state.selfPaced.profileImage
export let selectProfileImageStatus = (state: AppRootState) =>
  state.selfPaced.profileImageStatus

export let selectSeries = (state: AppRootState) => state.selfPaced.series
export let selectSeriesStatus = (state: AppRootState) =>
  state.selfPaced.seriesStatus
export let selectLinkedServiceSeries = (state: AppRootState) =>
  state.selfPaced.linkedServiceSeries
export let selectLinkedServiceSeriesStatus = (state: AppRootState) =>
  state.selfPaced.linkedServiceSeriesStatus
export let selectSeriesByID = (id: string) => (state: AppRootState) =>
  state.selfPaced.series.find((s) => s.id === id)
export let selectSeasonsInSeries = (id: string) => (state: AppRootState) =>
  uniq(
    state.selfPaced.series
      .find((s) => s.id === id)
      ?.courses.map((c) => c.season) ?? [],
  )

export let selectHeroSlides = (state: AppRootState) =>
  state.selfPaced.heroSlides
export let selectHeroSlidesStatus = (state: AppRootState) =>
  state.selfPaced.heroSlidesStatus

export let selectCourseProgress = (state: AppRootState) =>
  state.selfPaced.courseProgress
export let selectCourseProgressStatus = (state: AppRootState) =>
  state.selfPaced.courseProgressStatus
export let selectCourseProgressHasLoadedOnce = (state: AppRootState) =>
  state.selfPaced.courseProgressHasLoadedOnce
export let selectCourseProgressByCourseID =
  (id: string) => (state: AppRootState) =>
    state.selfPaced.courseProgress.find((p) => p.courseID === id)
export let selectCourseProgressByCourseIDMap = createSelector(
  selectCourseProgress,
  (progress) =>
    Object.fromEntries(progress.map((p) => [p.courseID, p] as const)),
)
export let selectActionableCourseProgress = createSelector(
  selectCourseProgress,
  (progress) => sortByUrgency(progress.filter(isActionableProgress)),
)

export let fetchClassesOnSectionChange =
  // eslint-disable-next-line consistent-return
  (store: any) => (next: any) => (action: any) => {
    if (action.type === 'selfPaced/fetchSectionsStatus/fulfilled') {
      store.dispatch(fetchClasses())
      store.dispatch(fetchSeries())
      store.dispatch(fetchLinkedServiceSeries())
      store.dispatch(fetchCompletedClasses())
      store.dispatch(fetchCourses())
      store.dispatch(fetchIncompleteAssignmentCourses())
      store.dispatch(fetchHeroSlides())
    }

    if (action) {
      return next(action)
    }
  }

export default selfPacedSlice.reducer
